import React from 'react';
import { Paperclip } from '@icon-park/react';
import { getProxyUrl } from '../../../utils/urlProxy';
import TextEditor from '../../../components/content-editors/TextEditor';
import ImageUploader from '../../../components/content-editors/ImageUploader';
import BackgroundEditor from '../../../components/content-editors/BackgroundEditor';
import VideoUploader from '../../../components/content-editors/VideoUploader';
import AudioUploader from '../../../components/content-editors/AudioUploader';
import LinkEditor from '../../../components/content-editors/LinkEditor';
import AttachmentEditor from '../../../components/content-editors/AttachmentEditor';
import DividerEditor from '../../../components/content-editors/DividerEditor';
import DividerContent from '../../../components/label-content/DividerContent';
import ProductBasicInfo from '../../../components/templates/ProductBasicInfo';
import NutritionTable from '../../../components/templates/NutritionTable';
import UsageMethod from '../../../components/templates/UsageMethod';
import RelatedLinks from '../../../components/templates/RelatedLinks';
import InspectionReport from '../../../components/templates/InspectionReport';

/**
 * 渲染编辑器
 */
export function renderEditor(selectedContentId, contentItems, handleUpdateContent) {
  if (!selectedContentId) return null;
  
  const selectedItem = contentItems.find(item => item.id === selectedContentId);
  if (!selectedItem) return null;

  const props = {
    data: { ...selectedItem.data },
    onUpdate: (newData) => handleUpdateContent(selectedItem.id, newData)
  };

  switch (selectedItem.type) {
    // 基础内容
    case 'text':
      return <TextEditor {...props} />;
    case 'image':
      return <ImageUploader {...props} />;
    case 'background':
      return <BackgroundEditor {...props} />;
    case 'video':
      return <VideoUploader {...props} />;
    case 'audio':
      return <AudioUploader {...props} />;
    case 'link':
      return <LinkEditor {...props} />;
    case 'attachment':
      return <AttachmentEditor {...props} />;
    case 'divider':
      return <DividerEditor {...props} />;
    case 'divider':
      return <DividerEditor {...props} />;
    // 业务模板
    case 'product-basic':
      return <ProductBasicInfo {...props} />;
    case 'nutrition':
      return <NutritionTable {...props} />;
    case 'usage':
      return <UsageMethod {...props} />;
    case 'related-links':
      return <RelatedLinks {...props} />;
    case 'inspection':
      return <InspectionReport {...props} />;
    default:
      return null;
  }
}

/**
 * 渲染预览内容
 */
export function renderPreviewContent(item) {
  switch (item.type) {
    case 'text':
      const hasContent = item.data.title || item.data.content;
      const hasBackgroundColor = item.data.backgroundColor && item.data.backgroundColor !== '#ffffff';
      
      return (
        <div 
          className="preview-text-content"
          style={{ backgroundColor: item.data.backgroundColor || 'transparent' }}
        >
          {item.data.title && (
            <div 
              className="preview-text-title"
              style={{ color: item.data.titleColor }}
              dangerouslySetInnerHTML={{ __html: item.data.title }}
            />
          )}
          {item.data.content && (
            <div 
              className="preview-text-body"
              style={{ color: item.data.contentColor }}
              dangerouslySetInnerHTML={{ __html: item.data.content }}
            />
          )}
          {!hasContent && hasBackgroundColor && (
            <div className="preview-text-placeholder">背景色预览</div>
          )}
          {!hasContent && !hasBackgroundColor && (
            <div className="preview-text-placeholder">请添加内容</div>
          )}
        </div>
      );
    
    case 'image':
      return (
        <div 
          className="preview-images"
          style={{ backgroundColor: item.data.backgroundColor || 'transparent' }}
        >
          {item.data.images && item.data.images.length > 0 ? (
            item.data.images.map((img, idx) => {
              const imgSrc = typeof img === 'string' ? img : img.src;
              const imgWidth = typeof img === 'string' ? 100 : (img.width || 100);
              const proxyImgSrc = getProxyUrl(imgSrc);
              return (
                <div 
                  key={idx} 
                  className="preview-image-wrapper"
                  style={{ width: `${imgWidth}%` }}
                >
                  <img 
                    src={proxyImgSrc} 
                    alt={`图片 ${idx + 1}`} 
                    className="preview-image"
                    onError={(e) => {
                      console.error('预览图片加载失败:', imgSrc);
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              );
            })
          ) : (
            <div className="preview-image-placeholder">
              {item.data.backgroundColor && item.data.backgroundColor !== '#ffffff' ? '背景色预览' : '暂无图片'}
            </div>
          )}
          {item.data.description && (
            <div className="preview-image-description">{item.data.description}</div>
          )}
        </div>
      );
    
    case 'video':
      return item.data.video ? (() => {
        const videoUrl = typeof item.data.video === 'string' ? item.data.video : item.data.video.url;
        const proxyVideoUrl = getProxyUrl(videoUrl);
        return (
          <div className="preview-video">
            <video 
              src={proxyVideoUrl} 
              controls 
              className="preview-video-player"
              onError={(e) => {
                console.error('预览视频加载失败:', videoUrl);
              }}
            />
          </div>
        );
      })() : null;
    
    case 'audio':
      return item.data.audio ? (() => {
        const audioUrl = typeof item.data.audio === 'string' ? item.data.audio : item.data.audio.url;
        const proxyAudioUrl = getProxyUrl(audioUrl);
        return (
          <div className="preview-audio">
            <audio 
              src={proxyAudioUrl} 
              controls 
              className="preview-audio-player"
              onError={(e) => {
                console.error('预览音频加载失败:', audioUrl);
              }}
            />
          </div>
        );
      })() : null;
    
    case 'link':
      return (
        <div className="preview-links">
          {item.data.links && item.data.links.map((link, idx) => (
            <div key={idx} className="preview-link-item">
              {link.icon && <span className="preview-link-icon">{link.icon}</span>}
              <span className="preview-link-name">{link.name}</span>
            </div>
          ))}
        </div>
      );
    
    case 'attachment':
      return (
        <div className="preview-attachments">
          {item.data.attachments && item.data.attachments.map((att, idx) => (
            <div key={idx} className="preview-attachment-item">
              <span className="preview-attachment-icon">
                <Paperclip theme="outline" size={16} fill="#333" strokeWidth={3} />
              </span>
              <span className="preview-attachment-name">{att.name}</span>
            </div>
          ))}
        </div>
      );
    case 'divider':
      return <DividerContent data={item.data} />;
    
    default:
      return <div className="preview-placeholder">暂无预览</div>;
  }
}
